/* eslint-disable no-unused-expressions */
import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from 'react-toastify';
import { API_TOKEN, API_URL } from "../../Config/secrets";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  uploadLoading: false,
  deleteLoading: false,
  error: null,
  records: {},
  totalRecords: 0,
};

const slice = createSlice({
  name: "aiKnowledgeBase",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    startUploadLoading(state) {
      state.uploadLoading = true;
    },
    stopUploadLoading(state) {
      state.uploadLoading = false;
    },
    startDeleteLoading(state) {
      state.deleteLoading = true;
    },
    stopDeleteLoading(state) {
      state.deleteLoading = false;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.uploadLoading = false;
      state.deleteLoading = false;
      state.error = action.payload;
    },
    // GET RECORDS SUCCESS
    getRecordsSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.totalRecords = action.payload.total;
      state.records = {
        ...state.records,
        [action.payload.page]: action.payload.data,
      };
    },
    // UPLOAD SUCCESS
    uploadSuccess(state) {
      state.uploadLoading = false;
      state.error = null;
    },
    // DELETE SUCCESS
    deleteSuccess(state) {
      state.deleteLoading = false;
      state.error = null;
    },
    resetAiKnowledgeBase: () => initialState,
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { resetAiKnowledgeBase } = slice.actions;

// ----------------------------------------------------------------------

const header = {
  Authorization: API_TOKEN,
};

/**
 * Get AI Knowledge Base records with filters
 * POST /ai-knowledge-base/get
 * @param {number} page - Page number (1-based)
 * @param {number} take - Records per page
 * @param {string} status - Filter by status (optional)
 * @param {string} date - Filter by exact date (optional)
 * @param {string} dateFrom - Filter from date (optional)
 * @param {string} dateTo - Filter to date (optional)
 * @param {boolean} hideLoader - Hide loader (optional)
 */
export function getAiKnowledgeBaseRecords(page = 1, take = 10, status = '', date = '', dateFrom = '', dateTo = '', hideLoader = false) {
  return async (dispatch) => {
    try {
      !hideLoader && dispatch(slice.actions.startLoading());

      const requestBody = {
        skip: page,
        take,
      };

      // Add optional filters if provided
      if (status && status.trim()) {
        requestBody.status = status.trim();
      }
      if (date && date.trim()) {
        requestBody.date = date.trim();
      }
      if (dateFrom && dateFrom.trim()) {
        requestBody.dateFrom = dateFrom.trim();
      }
      if (dateTo && dateTo.trim()) {
        requestBody.dateTo = dateTo.trim();
      }

      const response = await axios.post(`${API_URL}/ai-knowledge-base/get`, requestBody, {
        headers: header,
      });

      console.log('GET AI KNOWLEDGE BASE RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        dispatch(slice.actions.getRecordsSuccess({
          data: response?.data?.data?.data || [],
          total: response?.data?.data?.count || 0,
          page,
        }));
        return response?.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message || "Failed to fetch records"));
        toast.error(response?.data?.message || "Failed to fetch records", { autoClose: 3000 });
        return null;
      }
    } catch (error) {
      console.error('GET AI KNOWLEDGE BASE ERROR', error);
      dispatch(slice.actions.hasError(error?.response?.data?.message || error.message));
      toast.error(error?.response?.data?.message || "An error occurred", { autoClose: 3000 });
      return null;
    }
  };
}

/**
 * Upload files for AI training
 * POST /ai-knowledge-base/upload-data
 * @param {FileList|File[]} files - Files to upload
 * @param {string} trainingMode - Training mode/model identifier
 */
export function uploadAiKnowledgeBaseFiles(files, trainingMode) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startUploadLoading());

      const formData = new FormData();
      
      // Handle both FileList and array of files
      const fileArray = Array.isArray(files) ? files : Array.from(files);
      fileArray.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('training_mode', trainingMode);

      const response = await axios.post(`${API_URL}/ai-knowledge-base/upload-data`, formData, {
        headers: {
          ...header,
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('UPLOAD AI KNOWLEDGE BASE RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        dispatch(slice.actions.uploadSuccess());
        toast.success(response?.data?.message || `${fileArray.length} file(s) uploaded successfully`, { autoClose: 3000 });
        return response?.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message || "Failed to upload files"));
        toast.error(response?.data?.message || "Failed to upload files", { autoClose: 3000 });
        return null;
      }
    } catch (error) {
      console.error('UPLOAD AI KNOWLEDGE BASE ERROR', error);
      dispatch(slice.actions.hasError(error?.response?.data?.message || error.message));
      toast.error(error?.response?.data?.message || "An error occurred", { autoClose: 3000 });
      return null;
    }
  };
}

/**
 * Delete AI Knowledge Base records
 * POST /ai-knowledge-base/delete-records
 * @param {number[]} ids - Array of record IDs to delete
 */
export function deleteAiKnowledgeBaseRecords(ids) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startDeleteLoading());

      const response = await axios.post(`${API_URL}/ai-knowledge-base/delete-records`, {
        ids,
      }, {
        headers: header,
      });

      console.log('DELETE AI KNOWLEDGE BASE RESPONSE', response.data);

      if (response?.data?.statusCode === 200) {
        dispatch(slice.actions.deleteSuccess());
        toast.success(response?.data?.message || `${response?.data?.data?.deletedCount} record(s) deleted successfully`, { autoClose: 3000 });
        return response?.data;
      } else {
        dispatch(slice.actions.hasError(response?.data?.message || "Failed to delete records"));
        toast.error(response?.data?.message || "Failed to delete records", { autoClose: 3000 });
        return null;
      }
    } catch (error) {
      console.error('DELETE AI KNOWLEDGE BASE ERROR', error);
      dispatch(slice.actions.hasError(error?.response?.data?.message || error.message));
      toast.error(error?.response?.data?.message || "An error occurred", { autoClose: 3000 });
      return null;
    }
  };
}
